import {
  NavigationAction,
  StackActions,
  useNavigation,
} from '@react-navigation/native';
import {
  memo,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from 'react';
import { StyleProp, useWindowDimensions, View, ViewStyle } from 'react-native';
import { runOnJS, useAnimatedReaction } from 'react-native-reanimated';
import {
  SafeAreaInsetsContext,
  useSafeAreaInsets,
} from 'react-native-safe-area-context';
import { Subject } from 'rxjs';
import {
  BottomSheet,
  BottomSheetBackdrop,
  BottomSheetContext,
  BottomSheetProvider,
  OnBottomSheetDismiss,
  SnapBottomSheet,
  usePreventDragToDismissWhenMounted,
} from '../components/bottomSheet/BottomSheet';
import { UserInterfaceStyle } from './UserInterfaceIdiomContext';

export const BottomSheetWrapper = (props: {
  userInterfaceStyle?: UserInterfaceStyle;
  children: ReactNode;
  dismissible?: boolean;
  snapPoints?: number[];
  initialSnapToIndex?: number;
  dismissHandlerEnabled?: boolean;
  onDismiss?: () => void;
  snapTo$?: Subject<{
    index: number;
    callback?: (isFinished: boolean) => void;
  }>;
}) => {
  const {
    dismissHandlerEnabled = true,
    userInterfaceStyle = 'light',
    snapPoints,
    initialSnapToIndex,
    snapTo$,
    onDismiss,
  } = props;
  const { height } = useWindowDimensions();
  const { top, bottom } = useSafeAreaInsets();
  const handleDismiss = useCallback(() => {
    onDismiss?.();
  }, [onDismiss]);
  return (
    <BottomSheetProvider
      snapTo$={snapTo$}
      initialIndex={initialSnapToIndex}
      snapPoints={
        snapPoints ?? [0, Math.min(580, height - top - 20), height - top]
      }
    >
      <BottomSheetBackdrop
        mediumOpacity={userInterfaceStyle === 'dark' ? 0.65 : undefined}
        maxOpacity={userInterfaceStyle === 'dark' ? 0.9 : undefined}
      />
      <BottomSheet
        positioningMethod="position"
        css={`
          background-color: ${userInterfaceStyle === 'dark'
            ? '#262626'
            : '#fff'};
        `}
      >
        <SafeAreaInsetsContext.Provider
          value={{ top: 0, left: 0, right: 0, bottom }}
        >
          {props.children}
        </SafeAreaInsetsContext.Provider>
      </BottomSheet>
      <OnBottomSheetDismiss onDismiss={handleDismiss} />
      <SnapBottomSheet to={1} />
      {dismissHandlerEnabled && (
        <BottomSheetWrapperDismissHandler dismissible={props.dismissible} />
      )}
    </BottomSheetProvider>
  );
};

const BottomSheetWrapperDismissHandler = (props: { dismissible?: boolean }) => {
  usePreventDragToDismissWhenMounted(!props.dismissible);
  const navigation = useNavigation();
  const finished = useRef(false);
  const previousAction = useRef<NavigationAction>();
  const { pos, snapTo } = useContext(BottomSheetContext);
  useEffect(() => {
    return navigation.addListener('beforeRemove', e => {
      if (finished.current) {
        return;
      }
      e.preventDefault();
      previousAction.current = e.data.action;
      finished.current = true;
      snapTo(0);
    });
  }, [navigation, snapTo]);
  const onBottomSheetDismissed = useCallback(() => {
    finished.current = true;
    navigation.dispatch(previousAction.current ?? StackActions.pop());
  }, [navigation]);
  useAnimatedReaction(
    () => pos.value > 0,
    (result, previous) => {
      if (!result && previous) {
        runOnJS(onBottomSheetDismissed)();
      }
    },
    [onBottomSheetDismissed],
  );
  return null;
};

export const BottomSheetBarHandler = memo(
  ({
    style,
    userInterfaceStyle,
  }: {
    userInterfaceStyle?: UserInterfaceStyle;
    style?: StyleProp<ViewStyle>;
  }) => (
    <View
      css={`
        height: 20px;
        justify-content: center;
        align-items: center;
        background-color: ${userInterfaceStyle === 'dark' ? '#262626' : '#fff'};
      `}
      style={style}
    >
      <View
        css={`
          height: 6px;
          width: 57px;
          border-radius: 3px;
          background-color: #c4c4c4;
        `}
      />
    </View>
  ),
);
